import React, { useState, useEffect, useMemo } from 'react';
import { Modal } from 'react-bootstrap';
import { ChevronRight, Search } from 'lucide-react';
import Button from 'components/Button';
import { useRoleService } from 'services/role';
import { usePermissionData } from 'hooks/usePermissionData';
import { Role } from 'types/role';
import PermissionSelectorModal from './PermissionSelectorModal';
import styles from './Role.module.scss';

interface AddRoleModalProps {
  show: boolean;
  onHide: () => void;
  onSuccess: (role: Role) => void;
  onError: (message: string) => void;
}

const AddRoleModal: React.FC<AddRoleModalProps> = ({
  show,
  onHide,
  onSuccess,
  onError,
}) => {
  const roleService = useRoleService();
  const { permissions, loading: loadingPermissions } = usePermissionData();
  const [loading, setLoading] = useState(false);
  const [isSelectorOpen, setIsSelectorOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissionIds: [] as string[],
  });
  const [validated, setValidated] = useState(false);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!show) {
      setFormData({ name: '', description: '', permissionIds: [] });
      setValidated(false);
      setIsSelectorOpen(false);
    }
  }, [show]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const selectedPermissions = useMemo(() => {
    return permissions.filter((p) => formData.permissionIds.includes(p.id));
  }, [permissions, formData.permissionIds]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.permissionIds.length === 0) {
      setValidated(true);
      return;
    }

    setLoading(true);
    try {
      const newRole = await roleService.createRole(formData);
      onSuccess(newRole);
    } catch (error) {
      console.error('Failed to create role:', error);
      const message =
        error instanceof Error ? error.message : 'Failed to create role.';
      onError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Modal show={show && !isSelectorOpen} onHide={onHide} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Add Role</Modal.Title>
        </Modal.Header>
        <form onSubmit={(e) => void handleSubmit(e)}>
          <Modal.Body>
            <div className={styles.formGroup}>
              <label className={styles.label}>Role Name</label>
              <input
                type="text"
                name="name"
                className={styles.input}
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g. Sales Manager"
                required
              />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>Description</label>
              <textarea
                name="description"
                className={styles.textarea}
                value={formData.description}
                onChange={handleChange}
                placeholder="Enter role description..."
                rows={3}
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Permissions</label>
              <div
                className={styles.permissionTrigger}
                onClick={() => setIsSelectorOpen(true)}
              >
                <Search size={18} className={styles.triggerIcon} />
                <div className={styles.triggerValue}>
                  {selectedPermissions.length > 0 ? (
                    <>
                      {selectedPermissions.slice(0, 3).map((p) => (
                        <span key={p.id} className={styles.permissionBadge}>
                          {p.name}
                        </span>
                      ))}
                      {selectedPermissions.length > 3 && (
                        <span className={styles.permissionBadge}>
                          +{selectedPermissions.length - 3} more
                        </span>
                      )}
                    </>
                  ) : (
                    <span className={styles.placeholder}>
                      Select permissions...
                    </span>
                  )}
                </div>
                <ChevronRight size={18} />
              </div>
              {validated && formData.permissionIds.length === 0 && (
                <small
                  className="text-danger mt-1 d-block"
                  style={{ fontSize: '0.75rem' }}
                >
                  * At least one permission is required.
                </small>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              type="button"
              variant="outline"
              onClick={onHide}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading || loadingPermissions}>
              {loading ? 'Saving...' : 'Save Role'}
            </Button>
          </Modal.Footer>
        </form>
      </Modal>

      <PermissionSelectorModal
        show={isSelectorOpen}
        onHide={() => setIsSelectorOpen(false)}
        permissions={permissions}
        selectedPermissionIds={formData.permissionIds}
        onConfirm={(ids) =>
          setFormData((prev) => ({ ...prev, permissionIds: ids }))
        }
        loading={loadingPermissions}
      />
    </>
  );
};

export default AddRoleModal;
